/**
 * useDraftInfo — доступен ли черновик Seedance 2.5 и сколько стоят черновик и финал.
 *
 * Черновик — проба в 480p, из которой потом делается финал 1080p (backend: services/seedance_draft). Доступность решает
 * сервер: строки «Адаптеров» seedance-2.5-draft / -final активны и есть ключ BytePlus — выключили строку, и переключатель
 * «Черновик» у модели пропадает без выкатки фронта. Цены считает сервер теми же строками, что списывает (поэтому здесь
 * они не вычисляются — только показываются): число на кнопке и сумма списания не расходятся.
 *
 * Ответ зависит от длительности (цена идёт за секунду) и звука. Одинаковые запросы объединяются и держатся минуту: на
 * странице десятки карточек черновиков, а длительностей у них единицы.
 */
import { useEffect, useState } from 'react'
import { getDraftInfo } from '../../../api/images'

export interface DraftInfo {
	available: boolean
	/** Токенов за черновик такой длины; null — ещё не пришло. */
	draftCredits: number | null
	/** Токенов за финал 1080p из черновика такой длины; null — ещё не пришло. */
	finalCredits: number | null
	/** Сколько суток из черновика можно сделать финал. */
	ttlDays: number
}

export const NO_DRAFT: DraftInfo = { available: false, draftCredits: null, finalCredits: null, ttlDays: 7 }

const CACHE_MS = 60_000
const MIN_SECONDS = 4
const MAX_SECONDS = 30

const cache = new Map<string, { at: number; value: DraftInfo }>()
const inflight = new Map<string, Promise<DraftInfo>>()

/** Длительность, как её считает сервер: целое от 4 до 30 (у Seedance 2.5 нет меньше и больше). */
export function draftSeconds(duration: unknown): number {
	const n = Math.round(Number(duration))
	if (!Number.isFinite(n)) return 5
	return Math.min(MAX_SECONDS, Math.max(MIN_SECONDS, n))
}

export function fetchDraftInfo(duration: unknown, sound: boolean): Promise<DraftInfo> {
	const seconds = draftSeconds(duration)
	const key = `${seconds}:${sound ? 1 : 0}`
	const hit = cache.get(key)
	if (hit && Date.now() - hit.at < CACHE_MS) return Promise.resolve(hit.value)
	const pending = inflight.get(key)
	if (pending) return pending
	const request = getDraftInfo(seconds, sound)
		.then((r): DraftInfo => {
			const value: DraftInfo = r?.available
				? {
						available: true,
						draftCredits: typeof r.draft?.credits === 'number' ? r.draft.credits : null,
						finalCredits: typeof r.final?.credits === 'number' ? r.final.credits : null,
						ttlDays: r.ttl_days ?? 7,
					}
				: NO_DRAFT
			cache.set(key, { at: Date.now(), value })
			return value
		})
		// Сервер не ответил — как «недоступно», но не запоминаем: следующий вопрос спросит заново.
		.catch(() => NO_DRAFT)
		.finally(() => inflight.delete(key))
	inflight.set(key, request)
	return request
}

/** Сбросить кэш — для тестов и после смены аккаунта. */
export function resetDraftInfoCache(): void {
	cache.clear()
	inflight.clear()
}

/** enabled=false — модель без черновика: запросов нет, ответ «недоступно». Пока новый ответ идёт, держим прошлый:
 *  переключатель не мигает при смене длительности, а цена на мгновение остаётся прежней. */
export function useDraftInfo(enabled: boolean, duration: unknown, sound: boolean): DraftInfo {
	const [info, setInfo] = useState<DraftInfo>(NO_DRAFT)
	const seconds = draftSeconds(duration)
	useEffect(() => {
		// Модель без черновика: запросов нет, а ответ «недоступно» отдаёт return ниже — состояние не трогаем.
		if (!enabled) return
		let alive = true
		fetchDraftInfo(seconds, sound).then((value) => {
			if (alive) setInfo(value)
		})
		return () => {
			alive = false
		}
	}, [enabled, seconds, sound])
	return enabled ? info : NO_DRAFT
}
